import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { Section2Component } from '../../common/section2/section2.component';
import { AccountBackComponent } from '../back/account-back.component';
import { AccountInformationCentreService } from './account-information-centre.service';

export namespace NInformationCentreComponent {
    export const componentId = 'account-information-centre';

    export interface IAccountInformationCentreContent {
        title: string;
        items: IInformationCentreItem[];
    }

    export interface IInformationCentreItem {
        title: string;
        message: string;
        links?: IInformationCentreItemLink[];
        hide?: boolean;
    }

    export interface IInformationCentreItemLink {
        linkText: string;
        url: string;
        date: string;
    }

    export interface IItemsWithPublishedLinks {
        title: string;
        message: string;
        visibleLinks: IInformationCentreItemLink[];
        hiddenLinks: IInformationCentreItemLink[];
        hide?: boolean;
    }
}

@Component({
    selector: 'account-information-centre',
    templateUrl: './account-information-centre.html',
    styleUrls: ['./account-information-centre.scss'],
    standalone: true,
    imports: [AccountBackComponent, TranslationModule, Section2Component, HrefComponent, HideUpComponent, ToggleModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<AccountInformationCentreComponent>({
    id: NInformationCentreComponent.componentId,
    block: {
        url: NInformationCentreComponent.componentId
    },
    onDemandOnly: true,
    name: 'Account Information Centre',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'Information Centre',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'item',
            required: true,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    default: 'LeMieux Imagery',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'message',
                    title: 'Message',
                    default: 'Use the Image Libary to access studio and lifestyle for use on websites and social media.',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'link',
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'linkText',
                            title: 'Link text',
                            default: 'Image Library',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.textarea,
                            key: 'url',
                            title: 'Url',
                            required: true,
                            default: '/'
                        },
                        {
                            type: NICmsContentField.EFieldType.date,
                            key: 'date',
                            title: 'Date',
                            required: true,
                            default: formatDate(new Date(), 'yyyy-MM-dd', 'en-US')
                        }
                    ]
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'hide',
                    title: 'Hide block',
                    default: false
                }
            ]
        }
    ]
})
export class AccountInformationCentreComponent extends UiComponent implements OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NInformationCentreComponent.IAccountInformationCentreContent;

    private accountInformationCentreService = inject(AccountInformationCentreService);

    override ngOnInit(): void {
        this.accountInformationCentreService.resetCutsomerLastVisitedMessageBoardDate();
        super.ngOnInit();
    }

    protected get itemsWithPublishedLinks(): NInformationCentreComponent.IItemsWithPublishedLinks[] {
        return this.content?.items.map(el => {
            const currentDate = new Date();
            const publishedLinks = el?.links?.filter(el => {
                const elementDate = new Date(el.date);
                return elementDate <= currentDate;
            });

            return {
                title: el.title || '',
                message: el.message || '',
                hide: el.hide,
                visibleLinks: publishedLinks?.slice(0, 2) || [],
                hiddenLinks: publishedLinks?.slice(2) || []
            };
        }) || [];
    }
}
